<template>
  <div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import AuthProvider from '@/resources/AuthProvider'
import CxOneProvider from '@/resources/CxOneProvider'
import jwt from 'jsonwebtoken'
import { sendMessageToParent, isPopupWindow } from '@/utils/postMessage'
import { CXONE_MESSAGE_TYPES, CXONE_TIMEOUTS, CXONE_SKILL_MEDIA_TYPE } from '@/constants/cxone'
import { Logger } from '@/utils/logger'

const AuthService = new AuthProvider()
const CxOneService = new CxOneProvider()
const authLogger = Logger.create('CXOne Auth')

export default {
  mounted () {
    const query = this.$route.query || {}
    
    // ตรวจสอบ error จาก CXOne OAuth callback ก่อน
    if (query.error) {
      const errorMessage = query.error_description 
        ? decodeURIComponent(query.error_description) 
        : query.error || 'Authentication failed'
      
      this.handleError(errorMessage)
      return
    }
    
    // ตรวจสอบว่ามี code หรือไม่
    const code = query.code
    if (!code) {
      this.handleError('Missing authorization code')
      return
    }
    
    this.signIn(code)
  },
  methods: {
    ...mapActions({
      setUser: 'User/setUser'
    }),
    
    /**
     * Handle authentication error
     * @param {string} errorMessage - Error message to display
     */
    handleError (errorMessage) {
      authLogger.error('Auth Error:', errorMessage)
      
      // ส่ง error message กลับ parent ถ้าเป็น popup
      const messageSent = sendMessageToParent(
        CXONE_MESSAGE_TYPES.ERROR,
        { error: errorMessage },
        CXONE_TIMEOUTS.POPUP_CLOSE_DELAY_MS
      )
      
      if (!messageSent) {
        // ไม่ใช่ popup → redirect ไป login
        this.$router.push({ name: 'login' })
      }
    },
    
    /**
     * Main authentication flow
     * @param {string} code - Authorization code from CXOne
     */
    async signIn (code) {
      try {
        const authData = await this.authenticateWithCXOne(code)
        await this.setupUserSession(authData)
        await this.fetchUserSkills(authData.cxone?.accessToken)
        this.handleAuthSuccess(authData)
      } catch (error) {
        this.handleAuthFailure(error)
      }
    },
    
    /**
     * Authenticate with CXOne using authorization code
     * @param {string} code - Authorization code
     * @returns {Promise<Object>} Authentication data
     */
    async authenticateWithCXOne (code) {
      const { data } = await AuthService.signInCXOneWithCode(code)
      if (!data?.token) {
        throw new Error('No token received from authentication')
      }
      return data
    },
    
    /**
     * Setup user session in Vuex store
     * @param {Object} authData - Authentication data from API
     */
    setupUserSession (authData) {
      this.setUser({
        ...authData.user,
        token: authData.token
      })
      
      this.$store.dispatch('User/setRefreshToken', { 
        token: authData.cxone?.refreshToken 
      })
      this.$store.dispatch('User/setCXONELogin', { isLogin: true })
      
      const expired = Math.floor(Date.now() / 1000) + CXONE_TIMEOUTS.TOKEN_EXPIRATION_SECONDS
      this.$store.dispatch('User/setTokenExpired', { expired })
      this.$store.dispatch('User/setCXOneToken', { 
        token: authData.cxone?.accessToken 
      })
    },
    
    /**
     * Fetch user skills from CXOne
     * @param {string} cxOneToken - CXOne access token
     */
    async fetchUserSkills (cxOneToken) {
      if (!cxOneToken) return
      
      try {
        const decodedToken = jwt.decode(cxOneToken)
        const icAgentId = decodedToken?.icAgentId
        
        if (!icAgentId) {
          authLogger.warn('No icAgentId found in token')
          return
        }
        
        const skillsResponse = await CxOneService.skills(icAgentId)
        const agentSkillAssignments = skillsResponse?.data?.agentSkillAssignments
        
        if (Array.isArray(agentSkillAssignments)) {
          const skillData = agentSkillAssignments.filter(
            item => item?.mediaTypeId === CXONE_SKILL_MEDIA_TYPE.EMAIL
          )
          this.$store.dispatch('User/setCXOneSkill', { skill: skillData })
        }
      } catch (error) {
        // Log but don't fail auth if skills fetch fails
        authLogger.warn('Failed to fetch skills:', error)
      }
    },
    
    /**
     * Build auth payload for parent window (so parent can set store and stay logged in)
     * @param {Object} authData - Full auth data from API
     * @returns {Object} Payload for postMessage
     */
    buildAuthPayloadForParent (authData) {
      const tokenExpired = Math.floor(Date.now() / 1000) + CXONE_TIMEOUTS.TOKEN_EXPIRATION_SECONDS
      const skill = this.$store.getters['User/cxoneSkill']
      return {
        user: authData.user,
        token: authData.token,
        refreshToken: authData.cxone?.refreshToken ?? null,
        cxoneAccessToken: authData.cxone?.accessToken ?? null,
        tokenExpired,
        skill: skill || null
      }
    },

    /**
     * Handle successful authentication
     * @param {Object} authData - Full authentication data from API
     */
    handleAuthSuccess (authData) {
      const payload = this.buildAuthPayloadForParent(authData)
      const messageSent = sendMessageToParent(
        CXONE_MESSAGE_TYPES.SUCCESS,
        payload,
        CXONE_TIMEOUTS.POPUP_CLOSE_DELAY_MS
      )

      if (!messageSent) {
        // ไม่ใช่ popup → redirect ปกติ (fallback)
        this.$router.push({ name: 'dashboard' })
      }
    },
    
    /**
     * Handle authentication failure
     * @param {Error} error - Error object
     */
    handleAuthFailure (error) {
      const errorMessage = this.extractErrorMessage(error)
      authLogger.error('Auth Failure:', errorMessage, error)
      this.handleError(errorMessage)
    },
    
    /**
     * Extract error message from error object
     * @param {Error} error - Error object
     * @returns {string} Error message
     */
    extractErrorMessage (error) {
      if (error.response) {
        return error.response.data?.message 
          || error.response.data?.error 
          || error.message 
          || 'Internal Server Error'
      }
      return error.message || 'Authentication failed'
    }
  }
}
</script>
